import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchYdelserFromCMS, FALLBACK_YDELSER } from '../lib/cms';

export default function ServicesIndex() {
  const [ydelser, setYdelser] = useState(FALLBACK_YDELSER);

  useEffect(() => {
    async function loadData() {
      const cmsYdelser = await fetchYdelserFromCMS();
      if (cmsYdelser) setYdelser(cmsYdelser);
    }
    loadData();
  }, []);

  return (
    <div className="bg-[#dce2d7] text-[#1f241d]">
      <Helmet>
        <title>Vores Ydelser - PR Entreprise & Anlæg ApS</title>
        <meta
          name="description"
          content="Se alle vores ydelser inden for autoriseret kloak, anlæg, belægning, jordarbejde, gravearbejde, terrænregulering, indkørsler og terrasser i Ishøj."
        />
        <meta property="og:title" content="Vores Ydelser - PR Entreprise & Anlæg ApS" />
      </Helmet>

      {/* Header */}
      <section className="relative bg-[#141f14] text-white py-16 lg:py-20 border-b border-[#233323] overflow-hidden">
        <img
          src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/4a3bbf24-8a93-4bbf-a600-7956f25025f9/prentreprise-1.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-25 pointer-events-none"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#141f14] via-[#141f14]/90 to-transparent pointer-events-none" />
        <div className="relative max-w-[109rem] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-4 animate-fade-in-up">
            <span className="text-[#9ecc49] text-xs font-bold uppercase tracking-widest bg-[#3d5229] px-3 py-1 rounded-none border border-[#9ecc49]/30">
              Ydelser & Arbejdsområder
            </span>
            <h1 className="font-heading text-2xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
              Professionelle Løsninger inden for Entreprise, Anlæg & Kloak
            </h1>
            <p className="text-[#e8eee6] text-base sm:text-lg leading-relaxed">
              Vi tilbyder et komplet udvalg af autoriseret kloakarbejde, entreprenør- og anlægsopgaver for både private boligejere, erhvervsvirksomheder og offentlige bygherrer.
            </p>
          </div>
        </div>
      </section>

      {/* Grid of services */}
      <section className="py-20 bg-[#dce2d7] text-[#1f241d]">
        <div className="max-w-[109rem] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {ydelser.map((service) => (
              <Link
                key={service.service_slug}
                to={`/ydelser/${service.service_slug}`}
                className="group bg-white rounded-none overflow-hidden border border-[#cbd3c5] shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col cursor-pointer"
              >
                <div className="relative h-56 overflow-hidden bg-gray-900">
                  <img
                    src={service.image && service.image[0] ? `https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/${service.image[0].replace('https://assets.auxodev.dk/', '')}` : service.image[0]}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                  <span className="absolute bottom-3 left-3 text-[11px] font-bold uppercase tracking-widest text-[#9ecc49]">
                    VI HJÆLPER MED
                  </span>
                </div>

                <div className="p-6 flex-1 flex flex-col justify-between space-y-4">
                  <div>
                    <h2 className="font-heading font-bold text-lg sm:text-xl lg:text-2xl text-[#1f241d] group-hover:text-[#3d5229] transition-colors">
                      {service.title}
                    </h2>
                    <p className="text-[#4e564a] text-sm mt-2 leading-relaxed">
                      {service.summary}
                    </p>
                  </div>

                  <div className="pt-4 border-t border-[#cbd3c5] flex items-center justify-between font-heading font-bold text-sm text-[#3d5229] group-hover:text-[#2e3f1e]">
                    <span>Læs mere om {service.title}</span>
                    <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover:translate-x-1 text-[#9ecc49]" aria-hidden="true" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#141f14] text-white border-t border-[#233323]">
        <div className="max-w-4xl mx-auto px-4 text-center space-y-6">
          <span className="text-[#9ecc49] text-xs font-bold uppercase tracking-widest bg-[#3d5229] px-3 py-1 rounded-none inline-block">
            KONTAKT OS I DAG
          </span>
          <h2 className="font-heading text-xl sm:text-2xl lg:text-3xl font-bold text-[#9ecc49]">
            Brug for rådgivning eller et uforpligtende tilbud?
          </h2>
          <p className="text-white text-base">
            Ring direkte til os på <a href="tel:22231010" className="text-white font-bold underline whitespace-nowrap">Tlf. 22 23 10 10</a> eller send en besked via vores kontaktformular.
          </p>
          <div className="pt-2 flex flex-wrap justify-center gap-4">
            <a
              href="tel:22231010"
              className="bg-[#9ecc49] hover:bg-[#8ebb3c] text-[#1f241d] px-6 py-3.5 rounded-none font-heading font-bold uppercase tracking-wider text-sm inline-block shadow-lg"
            >
              Ring Tlf. 22 23 10 10
            </a>
            <Link
              to="/kontakt"
              className="bg-[#3d5229] hover:bg-[#2e3f1e] text-white px-6 py-3.5 rounded-none font-heading font-bold uppercase tracking-wider text-sm inline-block shadow-lg border border-[#9ecc49]/40"
            >
              Få et godt tilbud
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
